import React, { useState } from 'react';
import { useHistory } from 'react-router';
import {
  Box,
  FiservCard,
  FiservCardInterface,
  FiservCardStatusEnum,
  RightArrowIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { Roles } from '@/common/constants';
import ROUTES from '@/common/routes';
import { useHasAccess } from '@/modules/core/hooks';
import { CardItemActions } from '@/modules/transaction/components/Cards/components/CardItemActions';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import colors from '@/styles/colors';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import { useShipmentTrackerFeatureFlag } from '../../CardDetails/hooks';
import { CardItemWrapper } from '../Cards.styles';
import useCardReplaceModalStore from '../store/useCardReplaceModal.store';

const StyledBox = styled(Box)<{ expand: boolean }>`
  margin-bottom: ${({ theme, expand }) => (expand ? theme.spacings.spacing24 : 0)};

  &:last-child {
    margin-bottom: 0;
  }
`;
const ArrowIconWrap = styled(Box)<{ expand: boolean }>`
  transform: rotate(${({ expand }) => (expand ? '90deg' : '0deg')});
  color: ${({ theme }) => theme.colors.iconAccent};
`;

const TitleWrap = styled(Box)<{ expand: boolean }>`
  box-shadow: none;

  &:hover {
    color: ${({ theme }) => theme.colors.textAccent};
  }
`;

const STATUS_TITLE = {
  [FiservCardStatusEnum.ACTIVE]: 'Active Card',
  [FiservCardStatusEnum.FROZEN]: 'Locked Cards',
  [FiservCardStatusEnum.CLOSED]: 'Closed Cards',
  [FiservCardStatusEnum.PENDING_CLOSED]: 'Pending Closed Cards',
  [FiservCardStatusEnum.PENDING_FROZEN]: 'Pending Locked Cards',
  [FiservCardStatusEnum.PENDING_OPEN]: 'Pending Open Cards',
  [FiservCardStatusEnum.PENDING_REPLACE]: 'Pending Replace Cards',
  [FiservCardStatusEnum.PENDING_UNLOCK]: 'Pending Unlock Cards',
  [FiservCardStatusEnum.LOST]: 'Lost Cards',
  [FiservCardStatusEnum.STOLEN]: 'Stolen Cards',
  [FiservCardStatusEnum.NOT_SENT]: 'Not Sent Cards',
};

interface Props {
  status: FiservCardStatusEnum;
  cardList: FiservCardInterface[];
  expandable?: boolean;
  isLoading: boolean;
}

export const CardsSectionWrapper: React.FC<Props> = ({ expandable, cardList, status, isLoading }) => {
  const {
    state: { cardID },
  } = useCardReplaceModalStore();
  const { organizationProperties } = useOrganizationProperties();
  const { push } = useHistory();
  const [expand, setExpand] = useState(!expandable);

  const isCardShipmentAvailable = useShipmentTrackerFeatureFlag();
  const { isMobile } = useResponsive();
  const isEmployeeLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);

  return (
    <StyledBox expand={expand}>
      {expandable ? (
        <TitleWrap
          margin={{ horizontal: 'spacing8', bottom: 'spacing16' }}
          direction="row"
          align="center"
          onClick={() => setExpand(!expand)}
          expand={expand}
          data-testid="Card-section-wrapper"
        >
          <ArrowIconWrap expand={expand}>
            <RightArrowIcon color={expand ? colors.iconAccent : colors.iconPrimary} />
          </ArrowIconWrap>
          <Text margin={{ left: 'spacing16' }} size="xxlarge">
            {STATUS_TITLE[status]} ({cardList.length})
          </Text>
        </TitleWrap>
      ) : null}
      <Box direction={isMobile && isCardShipmentAvailable ? 'column' : 'row'} wrap gap="spacing8">
        {expand &&
          cardList.map((card) => (
            <CardItemWrapper
              isMobileCardListMode={isMobile && isCardShipmentAvailable}
              key={card.id}
              margin="spacing8"
              data-testId="card-section-list"
            >
              <Box
                onClick={() => {
                  if (isCardShipmentAvailable) {
                    return;
                  }
                  if (!isLoading) {
                    push(
                      `${ROUTES.CARD_DETAILS}?dependent_id=${card.holder.dependentId || ''}&card_id=${
                        card.id
                      }`
                    );
                  }
                }}
              >
                <FiservCard
                  isFullyMasked={isEmployeeLimitedAccess}
                  card={card}
                  isActive={card.id === cardID}
                  isLoading={isLoading}
                  logos={{
                    logoUrl: organizationProperties?.darkLargeLogoUrl,
                    cardLogo: whiteLabelingConfig?.images?.cardLogo,
                    defaultCardLogo: whiteLabelingConfig?.logo,
                    cardBackground: whiteLabelingConfig?.images?.cardBackground,
                  }}
                />
              </Box>
              {card.status !== FiservCardStatusEnum.CLOSED && (
                <Box margin={{ top: 'spacing12' }}>
                  <CardItemActions
                    isCardListMode={isCardShipmentAvailable}
                    isLoading={isLoading}
                    card={card}
                  />
                </Box>
              )}
            </CardItemWrapper>
          ))}
      </Box>
    </StyledBox>
  );
};
